import React from 'react';
import { Box, Skeleton, useTheme, useMediaQuery, Container } from '@mui/material';

/**
 * Skeleton loader for Footer that matches the exact layout.
 * @param {React.ReactNode} logo - Optional real Logo to show immediately (keeps branding visible while loading)
 */
export default function FooterSkeleton({ logo = null }) {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('lm'));

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Container maxWidth="100%" sx={{ p: "0 !important" }}>
        {/* Top Section Skeleton */}
        <Box
          sx={{
            [theme.breakpoints.up('md')]: {
              display: 'flex',
              flexDirection: 'column',
              pt: '66px',
              width: '100%',
              pb: '50px',
            },
            [theme.breakpoints.up('xs')]: {
              pt: '50px',
            },
            pl: { xs: '26px', lg: '17.5%' },
            pr: { xs: '26px', lg: '11%' },
            boxSizing: "border-box"
          }}
        >
          {/* Logo: real when provided, else skeleton */}
          <Box sx={{ mb: 4 }}>
            {logo ?? (
              <>
                <Skeleton
                  variant="rectangular"
                  width={273}
                  height={56}
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    borderRadius: '4px',
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  width={244}
                  height={50}
                  sx={{
                    display: { xs: 'block', lg: 'none' },
                    borderRadius: '4px',
                  }}
                />
              </>
            )}
          </Box>

          {/* Desktop Footer Columns Skeleton */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 4, mb: 4 }}>
            {[1, 2, 3, 4].map((col) => (
              <Box key={col} sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} height={28} sx={{ mb: 2 }} />
                {[1, 2, 3, 4, 5].map((item) => (
                  <Skeleton
                    key={item}
                    variant="text"
                    width={100}
                    height={20}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            ))}
          </Box>

          {/* Mobile Footer Sections Skeleton */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, pt: '46px', pb: '56px' }}>
            {[1, 2, 3, 4].map((section) => (
              <Box key={section} sx={{ mb: 3 }}>
                <Skeleton variant="text" width={150} height={24} sx={{ mb: 1 }} />
                {[1, 2, 3].map((item) => (
                  <Skeleton
                    key={item}
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{ mb: 0.5, ml: 2 }}
                  />
                ))}
              </Box>
            ))}
          </Box>

          {/* Social Icons Skeleton */}
          <Box
            sx={{
              display: { xs: 'flex', lg: 'flex' },
              justifyContent: { xs: 'center', lg: 'flex-end' },
              width: '100%',
              gap: 2,
              mt: { xs: 2, lg: -8 },
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((icon) => (
              <Skeleton
                key={icon}
                variant="circular"
                width={isSmallMobile ? 20 : 34}
                height={isSmallMobile ? 20 : 34}
              />
            ))}
          </Box>

          {/* Horizontal Bar Skeleton (Mobile) */}
          <Box
            sx={{
              display: { xs: 'block', lg: 'none' },
              mt: 3,
            }}
          >
            {[1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ mb: 1, borderRadius: '4px' }}
              />
            ))}
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ mt: { xs: "34px" } }}>
          <Box
            sx={{
              height: '0.5px',
              width: '100%',
              backgroundColor: '#FFFFFF',
              opacity: 0.3,
            }}
          />
        </Box>

        {/* Bottom Section Skeleton */}
        <Box
          sx={{
            [theme.breakpoints.down('md')]: {
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              py: { xs: 0 },
              padding: '48px 0 !important',
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: { xs: '0', md: '46px' },
              pl: { xs: '41px', lg: '17.5%' },
              pr: { xs: '41px', lg: '11%' },
              boxSizing: "border-box"
            }}
          >
            {/* Desktop Horizontal Links Skeleton */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 3 }}>
              {[1, 2, 3, 4].map((item) => (
                <Skeleton
                  key={item}
                  variant="text"
                  width={80}
                  height={20}
                />
              ))}
            </Box>

            {/* Image Skeleton (Desktop) */}
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Skeleton
                variant="rectangular"
                width={107}
                height={29}
                sx={{ borderRadius: '4px' }}
              />
            </Box>
          </Box>

          {/* Legal Text Skeleton */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: { xs: '0', md: 3 },
              pl: { xs: '41px', lg: '17.5%' },
              pr: { xs: '41px', lg: '11%' },
              boxSizing: "border-box"
            }}
          >
            <Skeleton
              variant="text"
              width="100%"
              height={60}
              sx={{ maxWidth: '910px' }}
            />
          </Box>

          {/* Image Skeleton (Mobile) */}
          <Box
            sx={{
              display: { xs: 'flex', lg: 'none' },
              mt: 3,
              pl: { xs: '41px', lg: '17.5%' },
              pr: { xs: '41px', lg: '11%' },
            }}
          >
            <Skeleton
              variant="rectangular"
              width={107}
              height={29}
              sx={{ borderRadius: '4px' }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
